import { useCallback, useEffect, useRef, useState } from "react";

import { Icon } from "./Icon.js";
import { Toggle } from "./Toggle.js";
import { ApiError } from "../api/client.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * La scheda SISTEMA, portata da `SettingsSystemTab.qml`.
 *
 * In cima lo stato in una riga, sotto le risorse, i comandi operativi, le
 * informazioni e il marchio che finisce sui PDF.
 *
 * **Le risorse sono quelle del SERVER**, non della postazione che guarda, ed
 * e' la differenza che rende utile questa schermata su un impianto: se il
 * ciclo di lettura rallenta, il motivo sta qui e non sul PC dell'operatore.
 *
 * Una cosa dell'app desktop non c'e' e non puo' esserci: la **modalita'
 * simulazione**. Li' avvia `python3` e ammazza processi sulla macchina
 * locale; su un server sarebbe un comando di sistema esposto dal browser, ed
 * e' esattamente il tipo di cosa che non si mette dietro una pagina web.
 */

interface Health {
  version?: string;
  uptime?: number;
  node?: string;
  cpu?: { usage: number; cores: number };
  memory?: { usage: number; used: number; total: number };
  database?: { engine: string; sizeBytes: number | null; latency: number; healthy: boolean };
  alarmStandard?: string;
}

interface Company {
  name: string;
  address: string;
  contact: string;
  logo: string;
}

const POLL_MS = 5000;

export function SystemTab() {
  const { t } = useI18n();
  const { api, can } = useSession();

  const [health, setHealth] = useState<Health>({});
  const [company, setCompany] = useState<Company>({ name: "", address: "", contact: "", logo: "" });
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);
  const [saved, setSaved] = useState(false);
  const logoRef = useRef<HTMLInputElement | null>(null);

  const fail = useCallback((e: unknown) => {
    if (e instanceof ApiError && e.isForbidden) setDenied(true);
    else setError(e instanceof ApiError ? e.message : String(e));
  }, []);

  const reload = useCallback(async () => {
    try {
      setHealth(await api.call<Health>("settings.health"));
      setError("");
      setDenied(false);
    } catch (e) {
      fail(e);
    }
  }, [api, fail]);

  useEffect(() => {
    void reload();
    // Le risorse hanno senso solo se si aggiornano: una percentuale ferma non
    // dice se la macchina sta reggendo.
    const timer = setInterval(() => void reload(), POLL_MS);
    return () => clearInterval(timer);
  }, [reload]);

  useEffect(() => {
    void api
      .call<Company>("settings.company")
      .then(setCompany)
      .catch(() => {
        /* il marchio richiede manageSystem: senza, la sezione resta vuota */
      });
  }, [api]);

  if (denied) {
    return (
      <div className="settings__pane">
        <p className="muted">{t("users.superAdminRequired")}</p>
      </div>
    );
  }

  const dbOk = health.database?.healthy !== false;
  const cpu = health.cpu?.usage ?? 0;
  const mem = health.memory?.usage ?? 0;
  // Lo stato globale e' il peggiore fra database e risorse: una macchina al
  // 95% di memoria non e' "ok" solo perche' il database risponde.
  const state = !dbOk ? "critical" : cpu > 90 || mem > 90 ? "warning" : "normal";
  const title =
    state === "critical"
      ? t("system.titleCritical")
      : state === "warning"
        ? t("system.titleWarning")
        : t("system.titleOk");

  return (
    <div className="settings__pane analytics">
      {error && <p className="alert">{error}</p>}

      <div className={`ahero ahero--${state}`}>
        <span className="ahero__dot">
          <i />
        </span>
        <div className="ahero__text">
          <h3>{title}</h3>
          <p className="muted small">
            {health.version ? `LMSmini v${health.version}` : t("system.warmingUp")}
            {health.node ? ` · Node ${health.node}` : ""}
          </p>
        </div>
        <div className="ahero__toggle">
          <span className="ametric__label">UPTIME</span>
          <b className="mono">{formatUptime(health.uptime)}</b>
        </div>
      </div>

      <h3 className="admin__title">{t("hdr.runtimeResources")}</h3>
      <div className="ametrics">
        <Gauge label="CPU" pct={cpu} sub={health.cpu ? `${health.cpu.cores} ${t("system.cores")}` : ""} />
        <Gauge
          label={t("settings.ramMemory")}
          pct={mem}
          sub={health.memory ? `${formatBytes(health.memory.used)} / ${formatBytes(health.memory.total)}` : ""}
        />
        <div>
          <div className="ametric__label">DATABASE</div>
          <div className="ametric__value">
            {health.database?.sizeBytes === null || health.database?.sizeBytes === undefined
              ? "—"
              : formatBytes(health.database.sizeBytes)}
          </div>
          <div className="ametric__sub">{health.database?.engine ?? "SQLite"}</div>
        </div>
      </div>

      {/* Due schede affiancate, come nel `.qml`: non due righe di un elenco.
          Ognuna ha icona, nome, la pastiglia con lo stato e sotto la riga che
          lo spiega — e la scheda si colora del proprio stato, che e' il
          motivo per cui sono schede e non righe. */}
      <h3 className="admin__title">{t("hdr.operationalControls")}</h3>
      <div className="opcards">
        <div className="opcard">
          <span className="opcard__icon">
            <Icon name="zap" size={22} />
          </span>
          <div className="opcard__text">
            <div className="opcard__top">
              <span className="opcard__name">{t("settings.simulationMode").toUpperCase()}</span>
              <span className="opcard__pill opcard__pill--ok">LIVE</span>
            </div>
            {/* Il comando c'e' nell'app desktop e qui non puo' esserci: sul
                desktop avvia `python3` e ammazza processi sulla macchina
                locale, che dietro una pagina web e' esattamente la cosa da non
                esporre. La levetta resta al suo posto **spenta e non
                premibile**: toglierla nasconderebbe la mancanza, lasciarla
                attiva darebbe un 404. La riga sotto e' quella del `.qml` e
                dice cosa farebbe; dire in parole *perche'* non c'e'
                richiederebbe una chiave di traduzione che l'app desktop non
                ha, e le chiavi qui sono generate da la'. */}
            <p className="muted small">{t("settings.simulationDesc")}</p>
          </div>
          <Toggle on={false} onChange={() => undefined} disabled />
        </div>

        <div className={`opcard opcard--${dbOk ? "ok" : "ko"}`}>
          <span className="opcard__icon">
            <Icon name="database" size={22} />
          </span>
          <div className="opcard__text">
            <div className="opcard__top">
              <span className="opcard__name">DATABASE</span>
              <span className={`opcard__pill opcard__pill--${dbOk ? "ok" : "ko"}`}>
                {dbOk ? t("focus.online").toUpperCase() : t("settings.error").toUpperCase()}
              </span>
            </div>
            <p className="muted small mono">
              {health.database?.engine ?? "SQLite"} · {health.database?.latency ?? 0}ms
            </p>
          </div>
        </div>
      </div>

      <h3 className="admin__title">{t("settings.systemInfo")}</h3>
      <div className="panel">
        <Info label={t("settings.softwareVersion")} value={`LMSmini v${health.version ?? "—"}`} />
        <Info label={t("settings.database")} value={health.database?.engine ?? "SQLite"} />
        <Info label={t("settings.alarmManagement")} value={health.alarmStandard ?? "ISA-18.2"} />
        <Info label={t("settings.telemetryFreq")} value="1 Hz" />
      </div>

      {can("manageSystem") && (
        <>
          <h3 className="admin__title">{t("pdfBranding")}</h3>
          <div className="panel">
            <div className="cgrid">
              <label className="cfield">
                <span>{t("companyName")}</span>
                <input value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} />
              </label>
              <label className="cfield">
                <span>{t("companyContact")}</span>
                <input value={company.contact} onChange={(e) => setCompany({ ...company, contact: e.target.value })} />
              </label>
              <label className="cfield cfield--wide">
                <span>{t("companyAddress")}</span>
                <input value={company.address} onChange={(e) => setCompany({ ...company, address: e.target.value })} />
              </label>
              {/* Il logo si CARICA, non si scrive: sul server un percorso
                  scritto a mano punterebbe a un file della macchina di chi
                  scrive, che il generatore dei PDF non vede. */}
              <label className="cfield cfield--wide">
                <span>{t("companyLogo")}</span>
                <div className="ptz__row">
                  <input
                    ref={logoRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (!f) return;
                      const reader = new FileReader();
                      reader.onload = () => setCompany({ ...company, logo: String(reader.result) });
                      reader.readAsDataURL(f);
                    }}
                  />
                  <button onClick={() => logoRef.current?.click()}>{t("uploadLogo")}</button>
                  {company.logo && <img className="logopreview" src={company.logo} alt="" />}
                  <span className="muted small">{t("msg.pngJpegMax")}</span>
                </div>
              </label>
            </div>
            <div className="ucard__buttons">
              {saved && <span className="ok small">✓</span>}
              <button
                className="btn--primary"
                onClick={() => {
                  void api
                    .call<Company>("settings.company", company)
                    .then((c) => {
                      setCompany(c);
                      setSaved(true);
                      setTimeout(() => setSaved(false), 2000);
                    })
                    .catch(fail);
                }}
              >
                {t("ptz.save")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Gauge({ label, pct, sub }: { label: string; pct: number; sub: string }) {
  const tone = pct > 90 ? "crit" : pct > 70 ? "warn" : "";
  return (
    <div>
      <div className="ametric__label">{label.toUpperCase()}</div>
      <div className={`ametric__value${tone ? ` ametric__value--${tone}` : ""}`}>{Math.round(pct)}%</div>
      <div className="gauge">
        <i style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} className={`gauge--${tone || "ok"}`} />
      </div>
      <div className="ametric__sub">{sub}</div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="row">
      <span className="muted">{label}</span>
      <span className="mono">{value}</span>
    </div>
  );
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatUptime(seconds: number | undefined): string {
  if (!seconds && seconds !== 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const p = (n: number) => String(n).padStart(2, "0");
  return d > 0 ? `${d}d ${p(h)}:${p(m)}:${p(s)}` : `${p(h)}:${p(m)}:${p(s)}`;
}
